// components/EnviarEmailModal/EnviarEmailModal.jsx
import { memo, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CircularProgress from "@mui/material/CircularProgress";

import "./EnviarEmailModal.scss";

/**
 * Modal reutilizável para envio de emails com templates
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - clientes: [{ id, nome, email, com_email }] — clientes seleccionados
 * - clinica: { clinica: { _id, nome, contacto } }
 * - sendRequest: função do useHttpClient
 * - auth: { token }
 * - contextoExtra: {} — dados extra para variáveis (ex: { valor_orcamento: "1000" })
 */
function EnviarEmailModal({
  open,
  onClose,
  clientes,
  clinica,
  sendRequest,
  auth,
  contextoExtra = {},
  endpoint = `${process.env.REACT_APP_BACKEND_LINK}/api/email/enviar`,
  extraBody = {},
  onEmailEnviado,
  tipo = null,
}) {
  const [templates, setTemplates] = useState([]);
  const [templateSelecionado, setTemplateSelecionado] = useState(null);
  const [preview, setPreview] = useState({ assunto: "", corpo: "" });
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState("");
  const [quotaMensal, setQuotaMensal] = useState(null);

  const clinicaId = clinica?.clinica?._id;
  const nomeClinica = clinica?.clinica?.nome || "";
  const contactoClinica = clinica?.clinica?.contacto || "";

  // ── Carregar templates ao abrir ─────────────────────────────────────────────
  useEffect(() => {
    if (!open || !clinicaId) return;
    setTemplateSelecionado(null);
    setPreview({ assunto: "", corpo: "" });
    setResultado(null);
    setErro("");
    carregarTemplates();
  }, [open, clinicaId]);

  const carregarTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/emailtemplates/${clinicaId}${tipo ? `?tipo=${tipo}` : ""}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );

      const statsResponse = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/email/stats/${clinicaId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );

      setQuotaMensal(statsResponse.mesAtual?.quota || null);
      setTemplates(response.templates || []);
    } catch {
      setErro("Erro ao carregar templates de email.");
    } finally {
      setLoadingTemplates(false);
    }
  };

  // ── Resolver variáveis do template para preview ─────────────────────────────
  const resolverVariaveis = (texto, nomeCliente = "") => {
    const dataHoje = new Date().toLocaleDateString("pt-PT");
    return texto
      .replace(/{{nome_cliente}}/g, nomeCliente || "Cliente")
      .replace(/{{nome_clinica}}/g, nomeClinica)
      .replace(/{{contacto_clinica}}/g, contactoClinica)
      .replace(/{{data_hoje}}/g, dataHoje)
      .replace(
        /{{(\w+)}}/g,
        (_, chave) => contextoExtra[chave] || `{{${chave}}}`,
      );
  };

  // ── Seleccionar template e gerar preview ────────────────────────────────────
  const handleSelecionarTemplate = (template) => {
    setTemplateSelecionado(template);
    const primeiroCliente = clientes[0]?.nome || "Cliente";
    setPreview({
      assunto: resolverVariaveis(template.assunto, primeiroCliente),
      corpo: resolverVariaveis(template.corpo, primeiroCliente),
    });
    setErro("");
    setResultado(null);
  };

  // ── Clientes válidos (têm email e com_email: true) ──────────────────────────
  const clientesValidos = clientes.filter((c) => c.email && c.com_email);
  const clientesSemEmail = clientes.filter((c) => !c.email || !c.com_email);

  // ── Enviar ──────────────────────────────────────────────────────────────────
  const handleEnviar = async () => {
    if (!templateSelecionado) {
      setErro("Selecciona um template antes de enviar.");
      return;
    }
    if (clientesValidos.length === 0) {
      setErro(
        "Nenhum cliente seleccionado tem email válido e permissão de envio.",
      );
      return;
    }

    setLoading(true);
    setErro("");

    try {
      const response = await sendRequest(
        endpoint,
        "POST",
        JSON.stringify({
          clinicaId,
          clienteIds: clientesValidos.map((c) => c.id),
          templateId: templateSelecionado._id,
          contextoExtra,
          ...extraBody,
        }),
        {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
        },
      );
      setResultado(response.resultado);
      if (response.resultado.status !== "falhou" && onEmailEnviado) {
        onEmailEnviado();
      }
    } catch (err) {
      setErro(err.message || "Erro ao enviar emails.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="enviar-email-modal__box">
        {/* Cabeçalho */}
        <div className="enviar-email-modal__cabecalho">
          <h2>Enviar Email</h2>
          <span className="enviar-email-modal__destinatarios">
            {clientesValidos.length} destinatário(s) válido(s)
            {clientesSemEmail.length > 0 && (
              <span className="enviar-email-modal__aviso">
                {" "}
                · {clientesSemEmail.length} sem email/permissão
              </span>
            )}
          </span>

          {quotaMensal && (
            <span style={{ fontSize: "12px", color: "#888" }}>
              📧 {quotaMensal.utilizado} / {quotaMensal.limite} emails este mês
            </span>
          )}
        </div>

        {/* Resultado do envio */}
        {resultado && (
          <div
            className={`enviar-email-modal__resultado enviar-email-modal__resultado--${resultado.status}`}
          >
            <p>
              {resultado.status === "completo" &&
                "✅ Todos os emails enviados com sucesso!"}
              {resultado.status === "parcial" &&
                `⚠️ Enviados ${resultado.enviados} de ${resultado.total}.`}
              {resultado.status === "falhou" && "❌ Falha ao enviar os emails."}
            </p>
            <p className="enviar-email-modal__quota">
              📧 Emails enviados este mês:{" "}
              {resultado.quotaMensal?.utilizadoAposEnvio}
              {/* /{" "} */}
              {/* {resultado.quotaMensal?.limite} */}
            </p>
            <button className="cancel-btn" onClick={onClose}>
              Fechar
            </button>
          </div>
        )}

        {!resultado && (
          <>
            {/* Lista de templates */}
            <div className="enviar-email-modal__templates">
              <h3>Escolhe um modelo</h3>

              {loadingTemplates && <CircularProgress size={24} />}

              {!loadingTemplates && templates.length === 0 && (
                <p className="enviar-email-modal__vazio">
                  Nenhum modelo de email criado para esta clínica.
                </p>
              )}

              {!loadingTemplates &&
                templates.map((t) => (
                  <div
                    key={t._id}
                    className={`enviar-email-modal__template-item ${templateSelecionado?._id === t._id ? "enviar-email-modal__template-item--activo" : ""}`}
                    onClick={() => handleSelecionarTemplate(t)}
                  >
                    <span className="enviar-email-modal__template-nome">
                      {t.nome}
                    </span>
                    <span className="enviar-email-modal__template-assunto">
                      {t.assunto}
                    </span>
                  </div>
                ))}
            </div>

            {/* Preview */}
            {templateSelecionado && (
              <div className="enviar-email-modal__preview">
                <h3>
                  Preview <span>(com dados do 1º cliente)</span>
                </h3>
                <div className="enviar-email-modal__preview-assunto">
                  <strong>Assunto:</strong> {preview.assunto}
                </div>
                <div className="enviar-email-modal__preview-corpo">
                  {preview.corpo}
                </div>
              </div>
            )}

            {/* Erro */}
            {erro && <p className="enviar-email-modal__erro">{erro}</p>}

            {/* Acções */}
            <div className="enviar-email-modal__acoes">
              <span className="cancel-btn" onClick={onClose}>
                Cancelar
              </span>
              <button
                className="submit-btn"
                onClick={handleEnviar}
                disabled={
                  loading ||
                  !templateSelecionado ||
                  clientesValidos.length === 0
                }
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  `Enviar para ${clientesValidos.length} cliente(s)`
                )}
              </button>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default memo(EnviarEmailModal);
